import { z } from "zod";
import { localDateSchema, timestampSchema, unitPriceSchema, uuidSchema } from "../common/primitives.js";
import { marketSalePriceSchema, marketUnitCostSchema } from "./enums.js";
/**
 * Satış fiyatı önerisi ve raf etiketi listesi (Prompt 16 aşama B, ADR 0016 §16, mevzuat sorusu #61).
 *
 * Öneri = birim maliyet × (1 + KDV) / (1 − hedef marj), market ayarındaki adıma YUKARI yuvarlanır. Hedef marj ürün
 * kategorisinden, yoksa market ayarından gelir. Öneri doğrudan fiyat YAZMAZ: seçilenler fiyat modülünde TASLAK fiyat
 * talebine dönüşür (onay ve yayın akışı mevcuttur).
 */
export const priceSuggestionsQuerySchema = z.object({
    stationId: uuidSchema,
    receiptId: uuidSchema.optional().describe("Yalnızca bu mal kabulün ürünleri"),
    belowTargetOnly: z
        .enum(["true", "false"])
        .optional()
        .describe("true → yalnızca mevcut marjı hedefin altında (ya da fiyatı olmayan) ürünler"),
});
export const priceSuggestionItemSchema = z.object({
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    categoryName: z.string().nullable(),
    vatRateCode: z.string(),
    vatRatePercent: z.string(),
    unitCost: marketUnitCostSchema.describe("Varsayılan depodaki hareketli ortalama (₺, KDV hariç)"),
    currentPrice: z.string().nullable().describe("Yürürlükteki istasyon fiyatı (KDV dâhil ₺)"),
    currentPriceValidFrom: timestampSchema.nullable(),
    currentMarginPercent: z.string().nullable(),
    targetMarginPercent: z.string(),
    targetMarginSource: z.enum(["CATEGORY", "SETTINGS"]),
    suggestedPrice: z.string().describe("Önerilen KDV dâhil fiyat (₺)"),
    suggestedMarginPercent: z.string(),
    belowTarget: z.boolean(),
});
export const priceSuggestionListSchema = z.object({
    stationId: uuidSchema,
    roundTo: z.string(),
    items: z.array(priceSuggestionItemSchema),
    skipped: z
        .array(z.object({ productId: uuidSchema, productName: z.string(), reason: z.string() }))
        .describe("Öneri üretilemeyen ürünler (maliyet yok, KDV oranı çözülemedi)"),
});
export const applyPriceSuggestionsSchema = z.object({
    stationId: uuidSchema,
    effectiveAt: timestampSchema.describe("Fiyat talebinin planlanan yürürlük zamanı"),
    note: z.string().trim().max(500).nullable().default(null),
    items: z
        .array(z.object({ productId: uuidSchema, newUnitPrice: marketSalePriceSchema }))
        .min(1, "En az bir ürün seçin")
        .max(1000)
        .refine((items) => new Set(items.map((item) => item.productId)).size === items.length, "Bir ürün yalnızca bir kez seçilebilir"),
});
// ── Etiket basım listesi ─────────────────────────────────────────────────────
export const labelListQuerySchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema.describe("Fiyatı bu günden (dâhil) itibaren yürürlüğe girenler"),
    to: localDateSchema.describe("Bu güne kadar (dâhil)"),
});
export const shelfLabelSchema = z.object({
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    brand: z.string().nullable(),
    barcode: z.string().nullable().describe("Birincil barkod"),
    oldPrice: unitPriceSchema.nullable().describe("Değişiklikten hemen önceki fiyat (KDV dâhil ₺)"),
    newPrice: unitPriceSchema.describe("Yürürlüğe giren fiyat (KDV dâhil ₺)"),
    effectiveAt: timestampSchema,
    shelfLocation: z.string().nullable(),
    netContent: z.string().nullable(),
    netContentUnit: z.string().nullable(),
    unitPrice: z
        .object({ per: z.enum(["KG", "L", "PIECE"]), price: z.string() })
        .nullable()
        .describe("Etiket birim fiyatı (₺/kg, ₺/L); net içerik yoksa null"),
    missingNetContent: z.boolean().describe("Net içerik tanımsız: etikette birim fiyat basılamaz (uyarı)"),
});
export const shelfLabelListSchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    items: z.array(shelfLabelSchema),
    missingNetContentCount: z.number().int(),
});
//# sourceMappingURL=pricing.js.map